import { atomField, AtomizedForm, required } from 'atoms/form-atoms';
import {
  ImportedEntityForm,
  IndividualEntityForm,
  OrganizationEntityForm,
} from './types';
import { FormRowStatus } from 'atoms/create-resource-atom';
import { EntityResponse } from 'types/api-responses';
import { OTHER_USER } from './flag-categories';
import type { FlagCategory } from 'types/flags';

export const FLAG_RATIONALE_REQUIRED_MESSAGE =
  'A rationale is required when the category is Other/User';

// A rationale is only mandatory for an Other/User flag. The validator is seeded
// from the entity's current category; the form swaps it (via set(field,
// { validate })) when the user changes the category — mirroring how the rest of
// the form does conditional validation.
export const flagRationaleValidator = (category: FlagCategory | null) => {
  return (value: string | null) => {
    if (category === OTHER_USER) {
      return required(value, FLAG_RATIONALE_REQUIRED_MESSAGE);
    }
    return value;
  };
};

export interface SeedAddress {
  // Existing record id, so edits route to the real record instead of a copy.
  id?: number;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  primary?: boolean;
}

export interface EntitySeedValues {
  name?: string | null;
  notes?: string | null;
  // `address` is a single seed (Change Match); `addresses`/`phoneNumbers`/
  // `emailAddresses` are full sets carrying ids (Canonize).
  address?: SeedAddress;
  addresses?: SeedAddress[];
  phoneNumbers?: Array<{
    id?: number;
    number?: string | null;
    primary?: boolean;
  }>;
  emailAddresses?: Array<{
    id?: number;
    email?: string | null;
    primary?: boolean;
  }>;
}

// Parse a full name into first/middle/last. Delimiters are whitespace and
// punctuation/special characters, EXCEPT hyphens and apostrophes — so
// "Smith-Jones" and "O'Brien" stay a single token. First token -> First,
// second -> Middle, remainder -> Last.
const splitName = (full?: string | null) => {
  if (!full) return { first: null, middle: null, last: null };
  const parts = full
    .trim()
    .split(/[^\p{L}\p{N}'-]+/u)
    .filter(Boolean);
  if (parts.length === 0) return { first: null, middle: null, last: null };
  if (parts.length === 1) return { first: parts[0], middle: null, last: null };
  if (parts.length === 2)
    return { first: parts[0], middle: null, last: parts[1] };
  return {
    first: parts[0],
    middle: parts.slice(1, -1).join(' '),
    last: parts[parts.length - 1],
  };
};

const seedAddressRow = (
  seed: SeedAddress | undefined,
  fallbackPrimary = true
) => {
  if (!seed) return null;
  if (!seed.streetAddress && !seed.city && !seed.state && !seed.zipCode) {
    return null;
  }
  return {
    id: atomField<number | undefined>(seed.id ?? undefined),
    streetAddress: atomField(seed.streetAddress ?? null),
    city: atomField(seed.city ?? null),
    state: atomField(seed.state ?? null),
    zipCode: atomField(seed.zipCode ?? null),
    primary: atomField<boolean>(seed.primary ?? fallbackPrimary),
    _rowStatus: atomField<FormRowStatus>(seed.id ? 'saved' : 'new'),
  };
};

const seededPhoneRow = (seed: {
  id?: number;
  number?: string | null;
  primary?: boolean;
}) => ({
  id: atomField<number | undefined>(seed.id ?? undefined),
  number: atomField(seed.number ?? null, {
    validate: (v) => required(v, 'Phone number is required'),
  }),
  primary: atomField<boolean>(seed.primary ?? false),
  _rowStatus: atomField<FormRowStatus>(seed.id ? 'saved' : 'new'),
});

const seededEmailRow = (seed: {
  id?: number;
  email?: string | null;
  primary?: boolean;
}) => ({
  id: atomField<number | undefined>(seed.id ?? undefined),
  email: atomField(seed.email ?? null, {
    validate: (v) => required(v, 'Email is required'),
  }),
  primary: atomField<boolean>(seed.primary ?? false),
  _rowStatus: atomField<FormRowStatus>(seed.id ? 'saved' : 'new'),
});

const defaultContactAttributes = {
  streetAddress: null,
  city: null,
  state: null,
  zipCode: null,
  phoneNumber: null,
  email: null,
  notes: null,
};

const phoneNumberRow = (el?: EntityResponse['phoneNumbers'][0]) => {
  return {
    id: atomField(el?.id),
    number: atomField(el?.number || null, {
      validate: (v) => required(v, 'Phone number is required'),
    }),
    primary: atomField(el?.primary || false),
    _rowStatus: atomField<FormRowStatus>(el ? 'saved' : 'new'),
  };
};

const emailRow = (el?: EntityResponse['emailAddresses'][0]) => {
  return {
    id: atomField(el?.id),
    email: atomField(el?.email || null, {
      validate: (v) => required(v, 'Email is required'),
    }),
    primary: atomField(el?.primary || false),
    _rowStatus: atomField<FormRowStatus>(el ? 'saved' : 'new'),
  };
};

const addressRow = (el?: EntityResponse['addresses'][0]) => {
  return {
    id: atomField(el?.id),
    streetAddress: atomField(el?.streetAddress || null),
    city: atomField(el?.city || null),
    state: atomField(el?.state || null),
    zipCode: atomField(el?.zipCode || null),
    primary: atomField(el?.primary || false),
    _rowStatus: atomField<FormRowStatus>(el ? 'saved' : 'new'),
  };
};

const seededAddresses = (entity?: EntityResponse, seed?: EntitySeedValues) => {
  if (entity) return entity.addresses.map(addressRow);
  // Prefer the full address set when present (seeding from a staged entity);
  // fall back to the single `address` seed used by existing callers.
  if (seed?.addresses?.length) {
    return seed.addresses.flatMap((a, i) => {
      const row = seedAddressRow(a, i === 0);
      return row ? [row] : [];
    });
  }
  const row = seedAddressRow(seed?.address);
  return row ? [row] : [];
};

const seededPhones = (entity?: EntityResponse, seed?: EntitySeedValues) => {
  if (entity) return entity.phoneNumbers.map(phoneNumberRow);
  return (seed?.phoneNumbers ?? []).map(seededPhoneRow);
};

const seededEmails = (entity?: EntityResponse, seed?: EntitySeedValues) => {
  if (entity) return entity.emailAddresses.map(emailRow);
  return (seed?.emailAddresses ?? []).map(seededEmailRow);
};

const seededNotes = (entity?: EntityResponse, seed?: EntitySeedValues) =>
  entity?.notes ?? seed?.notes ?? null;

// The consolidation-risk flag lives on the wrapper entity, so all three concrete
// types build it identically. New entities start unflagged.
const flagFields = (entity?: EntityResponse) => {
  const category = entity?.flagCategory ?? null;
  return {
    flagCategory: atomField<FlagCategory | null>(category),
    flagRationale: atomField<string | null>(entity?.flagRationale ?? null, {
      validate: flagRationaleValidator(category),
    }),
  };
};

const createIndividualEntityForm = (
  entity?: EntityResponse,
  seed?: EntitySeedValues
): AtomizedForm<IndividualEntityForm> => {
  const concreteEntityId = entity?.concreteEntity?.id;
  const split = entity ? null : splitName(seed?.name);
  return {
    concreteEntityAttributes: {
      id: atomField(concreteEntityId ?? null),
      firstName: atomField(
        entity?.concreteEntity.firstName || split?.first || null,
        {
          validate: (v) => required(v, 'First Name is required'),
        }
      ),
      middleName: atomField(
        entity?.concreteEntity.middleName || split?.middle || null
      ),
      lastName: atomField(
        entity?.concreteEntity.lastName || split?.last || null,
        {
          validate: (v) => required(v, 'Last Name is required'),
        }
      ),
      birthDate: atomField(entity?.concreteEntity.birthDate || null),
      deathDate: atomField(entity?.concreteEntity.deathDate || null),
    },
    notes: atomField(seededNotes(entity, seed)),
    ...flagFields(entity),
    phoneNumbersAttributes: atomField(seededPhones(entity, seed)),
    emailAddressesAttributes: atomField(seededEmails(entity, seed)),
    addressesAttributes: atomField(seededAddresses(entity, seed)),
  };
};

const createOrganizationEntityForm = (
  entity?: EntityResponse,
  seed?: EntitySeedValues
): AtomizedForm<OrganizationEntityForm> => {
  const concreteEntityId = entity?.concreteEntity?.id;
  return {
    concreteEntityAttributes: {
      id: atomField(concreteEntityId ?? null),
      primaryName: atomField(
        entity?.concreteEntity.primaryName || seed?.name || null,
        {
          validate: (v) => required(v, 'Primary Name is required'),
        }
      ),
      organizationType: atomField(
        entity?.concreteEntity.organizationType || null,
        {
          validate: (v) => required(v, 'Organization Type is required'),
        }
      ),
      formationDate: atomField(entity?.concreteEntity.formationDate || null),
      terminationDate: atomField(
        entity?.concreteEntity.terminationDate || null
      ),
    },
    // Organization copies the staged name to Primary Name (above), so notes
    // are not prefilled — only the Individual path mirrors the name to Notes.
    notes: atomField(entity?.notes || null),
    ...flagFields(entity),
    phoneNumbersAttributes: atomField(seededPhones(entity, seed)),
    emailAddressesAttributes: atomField(seededEmails(entity, seed)),
    addressesAttributes: atomField(seededAddresses(entity, seed)),
  };
};

const createImportedEntityForm = (
  entity?: EntityResponse,
  seed?: EntitySeedValues
): AtomizedForm<ImportedEntityForm> => {
  const concreteEntityId = entity?.concreteEntity?.id;
  return {
    concreteEntityAttributes: {
      id: atomField(concreteEntityId ?? null),
      name: atomField(entity?.name || seed?.name || null, {
        validate: (v) => required(v, 'Name is required'),
      }),
    },
    notes: atomField(entity?.notes || null),
    ...flagFields(entity),
    phoneNumbersAttributes: atomField(
      entity?.phoneNumbers.map(phoneNumberRow) || []
    ),
    emailAddressesAttributes: atomField(
      entity?.emailAddresses.map(emailRow) || []
    ),
    addressesAttributes: atomField(seededAddresses(entity, seed)),
  };
};

export {
  addressRow,
  createImportedEntityForm,
  createIndividualEntityForm,
  createOrganizationEntityForm,
  defaultContactAttributes,
  emailRow,
  phoneNumberRow,
};

// The server keys of the inputs that show a 422 inline; any other key belongs
// in a banner.
export const ENTITY_FORM_ERROR_KEY = {
  firstName: 'concreteEntity.firstName',
  middleName: 'concreteEntity.middleName',
  lastName: 'concreteEntity.lastName',
  primaryName: 'concreteEntity.primaryName',
} as const;

export const ENTITY_FORM_ERROR_FIELDS = Object.values(ENTITY_FORM_ERROR_KEY);
